import React from 'react';
import { useTable } from 'react-table';
import Pagination from '../../../shared/pagination/index';
import ContentLoader from 'react-content-loader';


const ReusableTableLoader = ({ columns, recordsPerPage = 9 }) => {
  const totalWidth = columns.reduce((total, column) => {
    return total + parseInt(column.style?.minWidth || '100');
  }, 0);

  return (
    <div className="w-full overflow-x-auto">
      <div style={{ minWidth: `${totalWidth}px` }}>
        <ContentLoader
          speed={2}
          width="100%"
          height={47}
          viewBox={`0 0 ${totalWidth} 47`}
          backgroundColor="#f3f3f3"
          foregroundColor="#ecebeb"
          className="w-full"
        >
          {columns.map((column, index) => {
            const previousWidths = columns
              .slice(0, index)
              .reduce((sum, col) => sum + parseInt(col.style?.minWidth || '100'), 0);
            
            const width = parseInt(column.style?.minWidth || '100');
            
            return (
              <rect 
                key={index}
                x={previousWidths + (index * 4)} 
                y="10"
                rx="4"
                ry="4"
                width={width - 8} 
                height="20"
              />
            );
          })}
        </ContentLoader>

        {Array.from({ length: recordsPerPage }).map((_, rowIndex) => (
          <ContentLoader
            key={rowIndex}
            speed={2}
            width="100%"
            height={62}
            viewBox={`0 0 ${totalWidth} 62`}
            backgroundColor="#f3f3f3"
            foregroundColor="#ecebeb"
            className="w-full border-b border-gray-200"
          >
            {columns.map((column, colIndex) => {
              const previousWidths = columns
                .slice(0, colIndex)
                .reduce((sum, col) => sum + parseInt(col.style?.minWidth || '100'), 0);
              
              const width = parseInt(column.style?.minWidth || '100');
              
              if (column.Header === 'Inhabilitar' || column.Header === 'Editar' || column.Header === 'Eliminar') {
                const centerX = previousWidths + (width / 2);
                return (
                  <React.Fragment key={colIndex}>
                    <rect 
                      x={previousWidths + 20}
                      y="15"
                      rx="4"
                      ry="4"
                      width={width - 40}
                      height="32"
                    />
                    {column.Header === 'Inhabilitar' && (
                      <circle cx={centerX} cy="31" r="8" />
                    )}
                    {column.Header === 'Editar' && (
                      <>
                        <rect x={centerX - 6} y="23" rx="1" ry="1" width="12" height="2" />
                        <rect x={centerX - 8} y="27" rx="1" ry="1" width="16" height="2" />
                        <rect x={centerX - 4} y="31" rx="1" ry="1" width="8" height="2" />
                      </>
                    )}
                    {column.Header === 'Eliminar' && (
                      <>
                        <rect x={centerX - 8} y="20" rx="1" ry="1" width="16" height="12" />
                        <rect x={centerX - 6} y="25" rx="0.5" ry="0.5" width="3" height="6" />
                        <rect x={centerX + 3} y="25" rx="0.5" ry="0.5" width="3" height="6" />
                      </>
                    )}
                  </React.Fragment>
                );
              }
              
              return (
                <rect 
                  key={colIndex}
                  x={previousWidths + 16}
                  y="25"
                  rx="3"
                  ry="3"
                  width={width - 32}
                  height="12"
                />
              );
            })}
          </ContentLoader>
        ))}
      </div>
    </div>
  );
};

const ReusableTable = ({
  columns,
  data,
  currentPage,
  setCurrentPage,
  recordsPerPage,
  totalRecords,
  loading,
  error
}) => {
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    initialState: { pageIndex: 0, pageSize: recordsPerPage || 10 },
  });

  if (error) {
    return (
      <div className='items-center justify-center w-full min-h-full pt-16'>
        <p className='pt-4 text-2xl text-center text-error font-itcmedium'>
          Hubo un error al recuperar los datos.
        </p>
      </div>
    );
  }

  if (loading) {
    return <ReusableTableLoader columns={columns} recordsPerPage={recordsPerPage} />;
  }

  return (
    <div className="w-full pt-4">
      {!error && data.length === 0 && (
        <div className='flex items-center justify-center w-full'>
          <p className='pt-4 text-xl text-center font-itcmedium'>No se hallaron registros.</p>
        </div>
      )}

      {data.length > 0 && (
        <>
          <div className="overflow-x-auto max-w-screen scroll-container organigrama-container">
            <table {...getTableProps()} className="w-full border-collapse ">
              <thead className="bg-white">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()} className="text-xs md:text-sm h-[47px] text-center text-blue font-itcbold">
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps()}
                        style={column.style}
                        className="px-4 py-2 border border-[#D9D9D9]"
                      >
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="table-row-group">
                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className={`text-xs md:text-sm h-[62px] border-collapse text-gray font-itcmedium text-center ${row.index % 2 === 0 ? 'bg-white' : 'bg-[#EBEBEB]/[0.2]'}`}
                    >
                      {row.cells.map(cell => (
                        <td
                          {...cell.getCellProps()}
                          className={`px-4 py-2 border border-[#D9D9D9] ${cell.column.id === 'id' || cell.column.id === 'fullName' ? 'font-itcmedium' : 'font-itcbook'}`}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col items-center justify-between mt-6 space-x-4 space-y-2 md:flex-row">

            <Pagination
              currentPage={currentPage}
              recordsPerPage={recordsPerPage}
              totalRecords={totalRecords}
              totalPages={totalPages}
              changePage={changePage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ReusableTable;
export { ReusableTableLoader };
